import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './booking/booking-shared.css';
import '../components/welfare/WelfareSignatures.css';
import AttachmentTile from '../components/welfare/AttachmentTile';
import { CONCERN_TYPE_LABEL, SEVERITY_LABEL } from '../components/welfare/welfareLabels';

const dateLabel = iso => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// Welfare report outcomes, or whole reports with their files, that the club's
// Welfare Officer or an admin has shared with this person (or with them as a
// gymnast's parent). They read each one and sign it as read.
export default function WelfareOutcomes() {
  const [shares, setShares] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/welfare/shared/mine')
      .then(res => setShares(res.data))
      .catch(() => setError('Could not load your messages. Check your signal and try again.'));
  }, []);

  const signed = (id, result) => setShares(list => list.map(s => (s.id === id ? { ...s, ...result } : s)));

  return (
    <div className="bk-page" style={{ maxWidth: 680 }}>
      <h2>Messages from the club</h2>
      <p className="bk-muted">The club's Welfare Officer has shared these with you. Please read each one and sign it to say you have read it.</p>
      {error && <p className="bk-error">{error}</p>}
      {!shares && !error && <p className="bk-muted">Loading…</p>}
      {shares?.length === 0 && <p>There's nothing to read.</p>}
      {shares?.map(share => <SharedOutcome key={share.id} share={share} onSigned={result => signed(share.id, result)} />)}
    </div>
  );
}

function SharedOutcome({ share, onSigned }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`/api/welfare/shared/${share.id}/acknowledge`, { fullName: name.trim() });
      onSigned(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not sign. Check your signal and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bk-card" style={{ marginBottom: '1rem' }}>
      <p className="bk-muted" style={{ margin: 0 }}>
        {share.gymnast ? `About ${share.gymnast.firstName} · ` : ''}{dateLabel(share.sharedAt)}
      </p>
      {share.message && <p style={{ whiteSpace: 'pre-wrap' }}>{share.message}</p>}
      {share.report && <SharedReport share={share} />}
      {share.acknowledgedAt ? (
        <p className="wshare-ok" style={{ margin: 0 }}>You signed this as read on {dateLabel(share.acknowledgedAt)}.</p>
      ) : (
        <form className="wsig-form" onSubmit={submit}>
          <label>
            Type your full name to sign that you have read this
            <input className="bk-input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
          </label>
          {error && <p className="bk-error">{error}</p>}
          <button type="submit" className="bk-btn bk-btn--primary bk-btn--sm" style={{ alignSelf: 'flex-start' }} disabled={busy || name.trim().length < 2}>
            {busy ? 'Signing…' : 'I have read this'}
          </button>
        </form>
      )}
    </section>
  );
}

const name = person => `${person.firstName} ${person.lastName}`;

// The whole report, as it was when it was shared
function SharedReport({ share }) {
  const { report, attachments } = share;
  const text = (title, value) => value && (
    <>
      <h4>{title}</h4>
      <p>{value}</p>
    </>
  );
  return (
    <div className="wshare-report">
      <dl>
        {report.about && <><dt>About</dt><dd>{name(report.about)}</dd></>}
        <dt>Date</dt><dd>{dateLabel(report.incidentDate)}</dd>
        {report.location && <><dt>Where</dt><dd>{report.location}</dd></>}
        <dt>Type</dt><dd>{CONCERN_TYPE_LABEL[report.concernType] ?? report.concernType}</dd>
        <dt>Severity</dt><dd>{SEVERITY_LABEL[report.severity] ?? report.severity}</dd>
        <dt>Reported by</dt><dd>{name(report.reportedBy)}</dd>
      </dl>
      {text('What happened', report.description)}
      {text('Action taken', report.actionTaken)}
      {text('Outcome', report.outcome)}
      {(report.witnessName || report.witnessStatement) && (
        <>
          <h4>Witness account{report.witnessName ? `: ${report.witnessName}` : ''}</h4>
          {report.witnessStatement && <p>{report.witnessStatement}</p>}
        </>
      )}
      {(report.referredExternally || report.referralDetails) && (
        <>
          <h4>External referral</h4>
          <p>{report.referredExternally ? 'Referred to an outside agency.' : 'Not referred to an outside agency.'}</p>
          {report.referralDetails && <p>{report.referralDetails}</p>}
        </>
      )}
      {attachments?.length > 0 && (
        <>
          <h4>Files</h4>
          <div className="wshare-files">
            {attachments.map(a => (
              <AttachmentTile
                key={a.id}
                attachment={a}
                load={() => axios.get(`/api/welfare/shared/${share.id}/attachments/${a.id}/file`, { responseType: 'blob' })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
